import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { ui } from '@procuros/datachecks';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { SuccessfulEnrichment } from 'hooks/useGruAgent/types';
import { SuggestionNavigationServiceSingleton } from 'services/suggestionNavigation';
import { useSuggestionChanges } from 'services/suggestionNavigation/hooks';
import { DisplayModes } from 'services/webedi/types';
import { humanizeString } from 'support/helpers/generic/generic';
import { MessageV2DTO, ProcessSpecificationDTO } from 'support/types';
import { Button } from 'components/Form/Button/Button';
import { Form } from 'components/Form/Form';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';

import { applyJqProgram } from './applyJqProgram';
import { AgentSuggestionSidebar } from './components/AgentSuggestionSidebar/AgentSuggestionSidebar';
import { SuggestionChangesBanner } from './components/SuggestionChangesBanner/SuggestionChangesBanner';

type AgentSuggestionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  message: MessageV2DTO | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  flattenData: Record<string, unknown> | undefined;
  uiConfig: Array<ui.Section> | undefined;
  suggestion: string | undefined;
  agentProposal: SuccessfulEnrichment | undefined;
};

export const AgentSuggestionModal = ({
  isOpen,
  onClose,
  onApply,
  message,
  processSpecFields,
  flattenData,
  uiConfig,
  suggestion,
  agentProposal,
}: AgentSuggestionModalProps) => {
  const [revisedCanonical, setRevisedCanonical] = useState<Record<string, unknown> | undefined>();

  useEffect(() => {
    if (!message?.canonical || !suggestion) {
      return;
    }

    let cancelled = false;
    applyJqProgram(message.canonical, suggestion).then((result) => {
      if (!cancelled) {
        setRevisedCanonical(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [message?.canonical, suggestion]);

  const { data: flattenRevisedData } = useFlattenDataAndUIConfig(processSpecFields, revisedCanonical, 'revised');

  const { t } = useTranslation();
  const formMethods = useForm<Record<string, unknown>>({ values: flattenRevisedData ?? flattenData });
  const numberOfChanges = useSuggestionChanges(flattenData, flattenRevisedData, uiConfig);

  const isLoading = !flattenData || !flattenRevisedData || !message || !agentProposal;
  const title = message
    ? t('webedi:suggestionModal.title', {
        documentType: humanizeString(message.type),
        reference: message.external_message_ref ?? '',
      }).trim()
    : '';

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 duration-300 ease-out data-closed:opacity-0" />

      <DialogPanel
        ref={containerRef}
        transition
        className="fixed inset-0 m-4 flex flex-col rounded-lg bg-white duration-300 ease-out data-closed:scale-95 data-closed:opacity-0"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} analyticsId="webedi:agent_suggestion_modal_cancel">
              {t('common:cancel')}
            </Button>
            <Button onClick={onApply} analyticsId="webedi:agent_suggestion_modal_apply">
              {t('webedi:sidebar.agents.applyButton')}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-sm text-gray-500">Loading...</div>
          </div>
        ) : (
          <FormProvider {...formMethods}>
            <SchemableFormNavigationContextProvider
              containerRef={containerRef}
              service={SuggestionNavigationServiceSingleton}
              NavigableItemPopoverComponent={null}
            >
              <div className="flex flex-1 overflow-hidden">
                <div className="flex flex-1 flex-col overflow-hidden">
                  <SuggestionChangesBanner numberOfChanges={numberOfChanges} />
                  <div className="flex-1 overflow-y-auto p-6">
                    <Form id="agent-suggestion-document">
                      <DocumentSections
                        mode={DisplayModes.view}
                        uiConfig={uiConfig}
                        documentType={message.type}
                        variables={
                          message.receiver_partner ? { receiverPartnerName: message.receiver_partner.name } : {}
                        }
                        baseData={flattenData}
                        revisedData={flattenRevisedData}
                      />
                    </Form>
                  </div>
                </div>
                <AgentSuggestionSidebar
                  agentProposal={agentProposal}
                  uiConfig={uiConfig}
                  canonical={message.canonical}
                />
              </div>
            </SchemableFormNavigationContextProvider>
          </FormProvider>
        )}
      </DialogPanel>
    </Dialog>
  );
};
